import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SessionProvider } from './lib/session';
import { AuthGate, useAuth } from './lib/auth-gate';
import { AppShell } from './components/AppShell';
import { Button, Card, ErrorNotice, Loading, ToastProvider } from './components/ui';
import { HomePage } from './pages/HomePage';
import { MyWorkPage } from './pages/MyWorkPage';
import { AccountsPage } from './pages/AccountsPage';
import { ContactsPage } from './pages/ContactsPage';
import { AccountDetailPage } from './pages/AccountDetailPage';
import { EnterprisesPage, EnterpriseDetailPage } from './pages/EnterprisePages';
import { ProjectsPage, ProjectDetailPage } from './pages/ProjectPages';
import { CommitmentsPage, FollowupsPage, CadencePage } from './pages/WorkListPages';
import { LeadershipPage } from './pages/LeadershipPage';
import { AiReviewPage } from './pages/AiReviewPage';
import { MatchReviewPage } from './pages/MatchReviewPage';
import { UsersRolesPage, OwnershipPage, SlaCadencePage, AuditPage } from './pages/AdminPages';
import { ApiError, getActingUser, setActingUser } from './lib/api';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // This is operational data: correctness matters more than avoiding a fetch.
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          {/* Decides dev vs SSO and, under SSO, blocks until a token exists —
              so no query below ever fires unauthenticated. */}
          <AuthGate>
            <SessionProvider fallback={(state) => <SessionFallback {...state} />}>
            <AppShell>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/my-work" element={<MyWorkPage />} />
                <Route path="/ai-review" element={<AiReviewPage />} />
                <Route path="/match-review" element={<MatchReviewPage />} />
                <Route path="/enterprises" element={<EnterprisesPage />} />
                <Route path="/enterprises/:enterpriseId" element={<EnterpriseDetailPage />} />
                <Route path="/accounts" element={<AccountsPage />} />
                <Route path="/accounts/:accountId" element={<AccountDetailPage />} />
                <Route path="/contacts" element={<ContactsPage />} />
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
                <Route path="/commitments" element={<CommitmentsPage />} />
                <Route path="/follow-ups" element={<FollowupsPage />} />
                <Route path="/cadence" element={<CadencePage />} />
                <Route path="/leadership" element={<LeadershipPage />} />
                <Route path="/admin/users" element={<UsersRolesPage />} />
                <Route path="/admin/ownership" element={<OwnershipPage />} />
                <Route path="/admin/sla-cadence" element={<SlaCadencePage />} />
                <Route path="/admin/audit" element={<AuditPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </AppShell>
            </SessionProvider>
          </AuthGate>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}

/**
 * Shown while resolving the session, and if it fails.
 *
 * In development the most likely failure is that the acting user in local storage
 * is not a user the API knows, so the recovery offered is to clear it.
 */
function SessionFallback({
  loading,
  error,
  retry,
}: {
  loading: boolean;
  error: unknown;
  retry: () => void;
}) {
  const auth = useAuth();

  if (loading) {
    return (
      <div className="grid min-h-full place-items-center">
        <Loading label="Signing you in" />
      </div>
    );
  }

  const actingUser = getActingUser();

  /*
   * Signed in with Microsoft, but no Aura account exists for that address.
   * Provisioning is deliberately manual (§43), so this is an ordinary state
   * with an ordinary answer — not a failure to debug, and not something
   * retrying will fix.
   */
  if (auth.signOut && error instanceof ApiError && error.status === 401) {
    return (
      <div className="grid min-h-full place-items-center p-6">
        <Card className="w-full max-w-lg">
          <h1 className="mb-2 text-sm font-semibold text-ink">You do not have an Aura account</h1>
          <p className="text-xs text-ink-2">
            You signed in successfully as{' '}
            <span className="font-mono">{auth.signedInAs}</span>, but no Aura account exists for
            that address. Accounts are created by an administrator rather than automatically, so
            ask one to add you and then sign in again.
          </p>
          <div className="mt-4">
            <Button variant="primary" onClick={auth.signOut}>
              Sign out
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid min-h-full place-items-center p-6">
      <Card className="w-full max-w-lg">
        <h1 className="mb-2 text-sm font-semibold text-ink">Could not sign you in</h1>
        <ErrorNotice error={error} />
        <div className="mt-4 space-y-3 text-xs text-ink-2">
          {!auth.signOut && actingUser && (
            <p>
              This browser is acting as <span className="font-mono">{actingUser}</span>. If that
              user does not exist, clear it and the API will fall back to its default.
            </p>
          )}
          {!auth.signOut && (
            <>
              <p>
                Check that the API is running on port 4000 and that the database has been migrated
                and seeded:
              </p>
              <pre className="overflow-x-auto rounded bg-surface-2 p-2 font-mono text-[11px]">
                {'pnpm db:setup && pnpm db:migrate && pnpm db:seed\npnpm dev'}
              </pre>
            </>
          )}
        </div>
        <div className="mt-4 flex gap-2">
          <Button variant="primary" onClick={retry}>
            Try again
          </Button>
          {actingUser && (
            <Button
              onClick={() => {
                setActingUser(null);
                window.location.reload();
              }}
            >
              Clear acting user
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
